import {
   array,
   boolean,
   description,
   isoTimestamp,
   maxLength,
   nullable,
   number,
   object,
   optional,
   picklist,
   pipe,
   record,
   regex,
   string,
   type InferOutput,
} from 'valibot'

import { ENTRY_ID_PATTERN } from './entry'
import { METRIC_IDS } from './registry'

/**
 * Valibot schemas for the `/v1/measurements` API. They document the transport
 * shape (the Worker renders them into OpenAPI) and give clients typed parsing;
 * the registry-aware rules (required fields per metric, no future timestamps,
 * positive values) live in `validateMeasurementEntry`.
 */

export const MAX_MEASUREMENT_ENTRIES_PER_REQUEST = 200

export const hashedUserIdSchema = pipe(
   string(),
   regex(/^[a-f0-9]{64}$/, 'userId must be a 64-char hex hash'),
   description('Hashed user identifier (64 hex characters).'),
)

export const metricIdSchema = pipe(
   picklist(METRIC_IDS as string[]),
   description(
      'Registered metric. `run`, `swim`, `cycle` and `row` record `distance` and `duration`; `sleep` records `duration`, which is absent while the sleep is still running.',
   ),
)

export const entryIdSchema = pipe(
   string(),
   regex(ENTRY_ID_PATTERN, 'entryId must be 8–64 characters of [A-Za-z0-9_-]'),
   description('Client-generated identifier (e.g. a UUID); stable across devices.'),
)

const isoDateTimeSchema = pipe(string(), isoTimestamp())

export const measurementEntrySchema = object({
   entryId: entryIdSchema,
   metricId: metricIdSchema,
   timestamp: pipe(
      isoDateTimeSchema,
      description(
         'When the activity happened (for sleep, when it started). Must not be in the future.',
      ),
   ),
   values: optional(
      pipe(
         record(string(), number()),
         description(
            'SI values keyed by the metric’s field keys: `distance` in metres and `duration` in seconds, positive. Exercise metrics require both; sleep has only `duration`, omitted while the sleep is running. Omitted for tombstones.',
         ),
      ),
   ),
   deletedAt: optional(
      pipe(
         nullable(isoDateTimeSchema),
         description('Set when the entry is a tombstone.'),
      ),
   ),
   updatedAt: pipe(
      isoDateTimeSchema,
      description('Last modification on the client; drives last-writer-wins.'),
   ),
})

export const measurementListResponseSchema = object({
   ok: boolean(),
   entries: array(measurementEntrySchema),
   serverTime: pipe(
      isoDateTimeSchema,
      description('Pass back as `since` on the next delta request.'),
   ),
})

export const measurementUpsertRequestSchema = object({
   userId: hashedUserIdSchema,
   entries: pipe(
      array(measurementEntrySchema),
      maxLength(
         MAX_MEASUREMENT_ENTRIES_PER_REQUEST,
         `entries may hold at most ${MAX_MEASUREMENT_ENTRIES_PER_REQUEST} items`,
      ),
   ),
})

export const measurementUpsertOutcomeSchema = object({
   entryId: pipe(
      nullable(string()),
      description('Null when the submitted entry had no usable id.'),
   ),
   status: picklist(['applied', 'stale', 'invalid']),
   error: optional(
      pipe(string(), description('Validation message; present only for `invalid`.')),
   ),
})

export const measurementUpsertResponseSchema = object({
   ok: boolean(),
   results: array(measurementUpsertOutcomeSchema),
   entries: pipe(
      array(measurementEntrySchema),
      description('The stored version of every valid submitted entry, winner or not.'),
   ),
   serverTime: isoDateTimeSchema,
})

export const measurementDeleteRequestSchema = object({
   userId: hashedUserIdSchema,
   entryId: entryIdSchema,
})

export const measurementDeleteResponseSchema = object({
   ok: boolean(),
   entry: measurementEntrySchema,
})

export type MeasurementListResponse = InferOutput<typeof measurementListResponseSchema>
export type MeasurementUpsertResponse = InferOutput<
   typeof measurementUpsertResponseSchema
>
export type MeasurementDeleteResponse = InferOutput<
   typeof measurementDeleteResponseSchema
>
